import { useMemo } from "react";
import { alpha, cardCls, ProgressRing, ProgressBar, greeting, fmt } from "./homeUi";

/**
 * Where the member stands on the level ladder.
 *
 * `me.level` is null below the FIRST level (the board's memberLevel() refuses to
 * call a 0-point member "Level 1"), so that case is worked out here: the target
 * is the first level and progress runs from 0 to its threshold.
 */
export function levelProgress(me) {
  const levels = Array.isArray(me?.levels) ? me.levels : [];
  if (!levels.length) return null;
  const points = Number(me?.points || 0);
  const cur = me?.level;

  if (!cur) {
    const first = levels[0];
    const need = Math.max(1, Number(first.points_required || 0));
    return {
      name: null,
      levelNo: 0,
      nextName: first.name,
      pct: Math.min(100, Math.round((points / need) * 100)),
      toNext: Math.max(0, need - points),
      floor: 0,
      ceil: need,
      isMax: false,
      total: levels.length,
    };
  }

  const curL = levels.find((l) => l.level_no === cur.level_no);
  const next = levels.find((l) => l.level_no === cur.level_no + 1);
  return {
    name: cur.level_name,
    levelNo: cur.level_no,
    nextName: cur.next_name,
    pct: cur.is_max ? 100 : cur.progress_pct,
    toNext: cur.points_to_next,
    floor: Number(curL?.points_required || 0),
    ceil: next ? Number(next.points_required || 0) : null,
    isMax: !!cur.is_max,
    total: levels.length,
  };
}

const STAT_DEFS = [
  {
    key: "posts",
    label: "Posts",
    menu: "community",
    tone: "text-violet-600 dark:text-violet-300 bg-violet-500/10",
    path: "M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z",
  },
  {
    key: "comments",
    label: "Comments",
    menu: "community",
    tone: "text-sky-600 dark:text-sky-300 bg-sky-500/10",
    path: "M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 0 1-.825-.242m9.345-8.334a2.126 2.126 0 0 0-.476-.095 48.64 48.64 0 0 0-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0 0 11.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155",
  },
  {
    key: "likes_received",
    label: "Likes received",
    menu: "community",
    tone: "text-rose-600 dark:text-rose-300 bg-rose-500/10",
    path: "M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z",
  },
  {
    key: "lessons_completed",
    label: "Lessons done",
    menu: "course",
    tone: "text-emerald-600 dark:text-emerald-300 bg-emerald-500/10",
    path: "M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z",
  },
];

// Tailwind needs whole class names, so the column count is looked up, not built.
const STAT_COLS = { 1: "grid-cols-1", 2: "grid-cols-2", 3: "grid-cols-1 sm:grid-cols-3", 4: "grid-cols-2 lg:grid-cols-4" };

export default function HomeHero({ me, stats, gamification, menuOn, primary }) {
  const lp = useMemo(() => levelProgress(me), [me]);
  const showPoints = !!gamification?.enabled;
  const streak = Number(me?.streak || 0);
  const firstName = me?.first_name || (me?.name || "").split(" ")[0] || "there";
  const statDefs = STAT_DEFS.filter((s) => menuOn(s.menu));
  const week = stats?.week || {};

  return (
    <section className={`${cardCls} relative overflow-hidden`} aria-label="Your progress">
      {/* Atmosphere, kept quiet: the brand colour is spent on the things that
          mean something (level, ring, bar), not painted across the card.
          - a hairline of brand light along the top edge,
          - the faintest brand tint fading down from it,
          - a neutral dot grid on the right, fading out toward the text.
          The grid takes its colour from the gray ramp, so it recedes the same
          way on light and dark. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent 5%, ${alpha(primary, 0.75)} 50%, transparent 95%)` }} />
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-40" style={{ background: `linear-gradient(180deg, ${alpha(primary, 0.06)}, transparent)` }} />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 right-0 w-3/5 text-gray-300"
        style={{
          backgroundImage: "radial-gradient(currentColor 1px, transparent 1.2px)",
          backgroundSize: "18px 18px",
          maskImage: "linear-gradient(to left, rgba(0,0,0,0.55), transparent 85%)",
          WebkitMaskImage: "linear-gradient(to left, rgba(0,0,0,0.55), transparent 85%)",
        }}
      />

      <div className="relative flex flex-col gap-6 p-5 sm:p-7">
        <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <p className="text-[13.5px] font-medium text-gray-500">{greeting()}</p>
            <h2 className="mt-1 text-[26px] font-extrabold leading-tight tracking-tight text-gray-900 sm:text-[30px]" style={{ textWrap: "balance" }}>
              Welcome back, {firstName}!
            </h2>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {showPoints && lp?.name ? (
                <span
                  className="inline-flex h-7 items-center gap-1.5 rounded-full px-3 text-[12.5px] font-bold"
                  style={{ backgroundColor: alpha(primary, 0.12), color: primary, boxShadow: `inset 0 0 0 1px ${alpha(primary, 0.3)}` }}
                >
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 21v-8m0 0c0-4 3-7 8-7 0 5-3 7-8 7Zm0 0C12 9 9 6 4 6c0 5 3 7 8 7" /></svg>
                  {lp.name}
                  {/* Named levels ("Seed") get their number; "Level 3" already says it. */}
                  {!/^level\b/i.test(lp.name) ? <span className="font-semibold opacity-70">· Level {lp.levelNo}</span> : null}
                </span>
              ) : null}
              {showPoints ? (
                <span className="inline-flex h-7 items-center gap-1 rounded-full border border-gray-200 bg-surface px-3 text-[12.5px] font-semibold text-gray-600">
                  <b className="font-extrabold text-gray-900">{fmt(me?.points)}</b> GP
                </span>
              ) : null}
              {streak > 0 ? (
                <span className="inline-flex h-7 items-center gap-1.5 rounded-full border border-orange-200 bg-orange-50 px-3 text-[12.5px] font-bold text-orange-600 dark:border-orange-500/30 dark:bg-orange-500/10 dark:text-orange-300" title="Days in a row you've been active">
                  <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2s5 5 5 10a5 5 0 0 1-10 0c0-2 1-3.5 1-3.5S9 11 11 11c0-4 1-9 1-9Z" /></svg>
                  {streak}-day streak
                </span>
              ) : null}
              {showPoints && me?.rank ? (
                <span className="inline-flex h-7 items-center gap-1 rounded-full border border-gray-200 bg-surface px-3 text-[12.5px] font-semibold text-gray-600">
                  Rank <b className="font-extrabold text-gray-900">#{me.rank}</b>
                  {me.total ? <span className="text-gray-400">of {fmt(me.total)}</span> : null}
                </span>
              ) : null}
            </div>
          </div>

          {showPoints && lp ? (
            <div
              className="flex shrink-0 items-center gap-4 self-start rounded-2xl border border-gray-200 bg-surface/90 py-3 pl-3 pr-5 backdrop-blur"
              style={{ boxShadow: `0 1px 2px rgba(16,24,40,0.05), 0 12px 32px -18px ${alpha(primary, 0.55)}` }}
            >
              <ProgressRing pct={lp.pct} primary={primary}>
                <span className="text-[15px] font-extrabold tabular-nums text-gray-900">{lp.isMax ? "MAX" : `${lp.pct}%`}</span>
              </ProgressRing>
              <div className="min-w-0">
                {lp.isMax ? (
                  <>
                    <p className="text-[12px] font-medium text-gray-500">Top level</p>
                    <p className="truncate text-[16px] font-bold text-gray-900">{lp.name}</p>
                    <p className="text-[12.5px] text-gray-500">You've reached the summit</p>
                  </>
                ) : (
                  <>
                    <p className="text-[12px] font-medium text-gray-500">Next level</p>
                    <p className="max-w-[11rem] truncate text-[16px] font-bold text-gray-900">{lp.nextName}</p>
                    <p className="text-[12.5px] text-gray-500">{fmt(lp.toNext)} GP to go</p>
                  </>
                )}
                {/* The ladder at a glance: one notch per level, filled up to here. */}
                <div className="mt-2 flex items-center gap-1" aria-label={`Level ${lp.levelNo} of ${lp.total}`}>
                  {Array.from({ length: Math.min(lp.total, 8) }).map((_, i) => (
                    <span
                      key={i}
                      className="h-1 w-4 rounded-full bg-gray-200"
                      style={i < lp.levelNo ? { backgroundColor: primary } : undefined}
                    />
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {showPoints && lp && !lp.isMax ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-[12px] font-medium tabular-nums text-gray-500">
              <span>
                <b className="font-semibold text-gray-800">{lp.name || "Start"}</b> · {fmt(me?.points)} GP
              </span>
              <span>
                {lp.nextName} · {fmt(lp.ceil)} GP
              </span>
            </div>
            <ProgressBar pct={lp.pct} primary={primary} />
          </div>
        ) : null}

        {statDefs.length ? (
          <div className={`grid gap-3 border-t border-gray-200 pt-5 ${STAT_COLS[statDefs.length]}`}>
            {statDefs.map((s) => {
              const delta = Number(week[s.key] || 0);
              return (
                // Stacked on phones (two tiles to a row leave no room for icon +
                // label side by side), a row from sm up.
                <div key={s.key} className="relative flex flex-col items-start gap-2.5 rounded-2xl border border-gray-200 bg-gray-50/70 px-4 py-3.5 sm:flex-row sm:items-center sm:gap-3">
                  <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${s.tone}`}>
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7"><path strokeLinecap="round" strokeLinejoin="round" d={s.path} /></svg>
                  </span>
                  <div className="min-w-0">
                    <p className="text-[22px] font-extrabold leading-none tabular-nums text-gray-900">{fmt(stats?.[s.key])}</p>
                    <p className="mt-1 text-[12px] font-medium leading-tight text-gray-500">{s.label}</p>
                  </div>
                  {delta > 0 ? (
                    <span className="absolute right-3 top-3 shrink-0 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-bold text-emerald-600 dark:text-emerald-300 sm:static sm:ml-auto" title="In the last 7 days">
                      +{fmt(delta)}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </section>
  );
}
